import { FC, ReactNode, useState } from "react";

import {
  ThemeToggle,
  useResolvedIsDark,
} from "@tsmono/inspect-components/theme";
import { LoadingBar } from "@tsmono/react/components";
import { isVscode } from "@tsmono/util";

import { useSelectedLogLoading } from "../../state/selectedLogDetails";
import { useStore } from "../../state/store";
import { useUserSettings } from "../../state/userSettings";
import { ViewerOptionsButton } from "../log-list/ViewerOptionsButton";
import { ViewerOptionsPopover } from "../log-list/ViewerOptionsPopover";

import { Navbar } from "./Navbar";

interface ApplicationNavbarProps {
  currentPath: string | undefined;
  fnNavigationUrl: (file: string, log_dir?: string) => string;
  backUrl?: string;
  homeUrl?: string;
  bordered?: boolean;
  children?: ReactNode;
  breadcrumbsEnabled?: boolean;
  /** Extra loading signal for the activity bar (e.g. the log listing
   *  syncing), ORed with the selected log's own loading state. */
  loading?: boolean;
  /** Non-blocking listing refresh failure shown on the viewer options
   *  affordance while previously loaded content remains visible. */
  viewerOptionsError?: Error;
}

export const ApplicationNavbar: FC<ApplicationNavbarProps> = ({
  currentPath,
  fnNavigationUrl,
  backUrl,
  homeUrl,
  bordered,
  children,
  breadcrumbsEnabled,
  loading: loadingProp = false,
  viewerOptionsError,
}) => {
  const [optionsEl, setOptionsEl] = useState<HTMLButtonElement | null>(null);
  const themePreference = useUserSettings((s) => s.themePreference);
  const setThemePreference = useUserSettings((s) => s.setThemePreference);
  const isDark = useResolvedIsDark(themePreference);
  const loading = useSelectedLogLoading() || loadingProp;

  const isShowing = useStore((state) => state.app.dialogs.options);
  const setShowing = useStore(
    (state) => state.appActions.setShowingOptionsDialog
  );

  return (
    <div>
      <Navbar
        currentPath={currentPath}
        fnNavigationUrl={fnNavigationUrl}
        backUrl={backUrl}
        homeUrl={homeUrl}
        bordered={bordered}
        breadcrumbsEnabled={breadcrumbsEnabled}
      >
        {children}
        <ThemeToggle
          value={themePreference}
          isDark={isDark}
          onChange={setThemePreference}
          hideModeSwitch={isVscode()}
        />
        <ViewerOptionsButton
          showing={isShowing}
          setShowing={setShowing}
          error={viewerOptionsError}
          ref={setOptionsEl}
        />
        <ViewerOptionsPopover
          positionEl={optionsEl}
          showing={isShowing}
          setShowing={setShowing}
          error={viewerOptionsError}
        />
      </Navbar>
      <LoadingBar loading={loading} />
    </div>
  );
};
